import { useEffect } from 'react'
import posthog from 'posthog-js'

import DotsLoading from 'components/loadings/dotsLoading'
import { resolvePlatform } from 'modules/miniapp/detect'
import type { MiniappPlatform } from 'modules/miniapp'
import type { ShareStage } from 'components/miniapp/shareNumberButton/useShareNumber'

const PLATFORM_NAMES: Record<MiniappPlatform, string> = {
    eitaa: 'ایتا',
    bale: 'بله',
    telegram: 'تلگرام',
}

interface Props {
    stage: ShareStage
    /** The number the messenger handed back, once it has. */
    phone: string | null
    disabled?: boolean
    onShare: () => void
    /** Start the ordinary SMS login for the number the messenger gave us. */
    onUseOtp: (phone: string) => void
    /** «با شمارهٔ دیگری وارد شوید» — show the phone form instead. */
    onOtherNumber: () => void
}

/**
 * The login screen of someone who opened Hamyan INSIDE a messenger.
 *
 * One big green button and almost nothing else, on purpose. The messenger
 * already knows this person's number, so signing in is one tap, costs us no
 * SMS, and — the part that matters most — leaves their messenger account
 * linked, which is what lets Hamyan message them there instead of texting.
 *
 * It used to be the other way round: the phone form came first with a small
 * outlined «استفاده از شمارهٔ همین حساب» under it, and people did what the
 * screen told them to. On 2026-09-17, 90 of 103 Eitaa accounts and 87 of 131
 * Bale accounts had been created by typing a number and waiting for a code.
 *
 * Typing a number is still possible — someone's fund may be under a different
 * number than their messenger — but it is a quiet link under the button, not a
 * peer of it.
 */
export default function MessengerLogin({
    stage,
    phone,
    disabled,
    onShare,
    onUseOtp,
    onOtherNumber,
}: Props) {
    const platform = resolvePlatform()
    const name = platform ? PLATFORM_NAMES[platform] : 'پیام‌رسان'

    useEffect(() => {
        posthog.capture('miniapp_login_hero_shown', { platform })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const otherNumber = (
        <button
            type="button"
            onClick={() => {
                posthog.capture('miniapp_login_other_number_tapped', {
                    platform,
                    stage,
                })
                onOtherNumber()
            }}
            className="mt-6 text-xs text-text-gray underline underline-offset-4"
        >
            با شمارهٔ دیگری وارد شوید
        </button>
    )

    if (stage === 'requesting' || stage === 'waiting') {
        return (
            <div className="mx-5 mt-10 flex flex-col items-center gap-3 text-center">
                <DotsLoading />
                <p className="text-base">در حال ورود به همیان…</p>
                <p className="text-xs text-text-gray leading-6">
                    شمارهٔ شما در حال بررسی است. چند لحظه صبر کنید.
                </p>
            </div>
        )
    }

    if (stage === 'fallback') {
        return (
            <div className="mx-5 mt-10 flex flex-col items-center text-center">
                <p className="text-sm text-text-gray leading-7 mb-4">
                    ورود خودکار انجام نشد. می‌توانید با کد پیامکی وارد شوید.
                </p>
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                        posthog.capture('miniapp_share_number_otp_fallback', {
                            variant: 'hero',
                        })
                        // `phone` is guaranteed here: stage only becomes
                        // 'fallback' when we actually have a number.
                        if (phone) onUseOtp(phone)
                    }}
                    className="w-full bg-green-1 text-white rounded-xl py-4 text-base font-bold disabled:opacity-60"
                >
                    ورود با کد پیامکی
                </button>
                {otherNumber}
            </div>
        )
    }

    return (
        <div className="mx-5 mt-10 flex flex-col items-center text-center">
            <button
                type="button"
                onClick={onShare}
                disabled={disabled}
                className="w-full bg-green-1 text-white rounded-xl py-4 text-lg font-bold shadow-md active:opacity-90 disabled:opacity-60"
            >
                ورود با {name}
            </button>
            <p className="mt-3 text-xs text-text-gray leading-6">
                با یک لمس؛ بدون وارد کردن شماره و بدون کد پیامکی.
            </p>
            {otherNumber}
        </div>
    )
}
